import React, { createContext, useContext, useMemo, useState } from 'react';
import { NavItem } from '../interfaces/navigation';
import { BreadcrumbGroup } from '@amzn/awsui-components-react-v2';
import { LinkTypes, NavType } from '../constants/navigation';
import { Roles } from '../constants/auth';
import { check } from '../utils/auth';
import { FeatureFlags } from '../interfaces/featureFlags';
import { checkFeature } from '../utils/featureFlag';
import { usePersistedState } from '../hooks/usePersistedState';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorage';
import { NavState, isNavState } from '../interfaces/persistedState';

export interface NavData {
    navItems: NavItem[];
    navItemsMap: Map<string, NavItem>;
}
export interface UseNav {
    navData: NavData;
    breadcrumbs: BreadcrumbGroup.Item[];
    sideNavEnabled: boolean;
    isNavOpened: boolean;
}

export interface UseNavActions {
    generateBreadcrumbs: (path: string, params: any) => void;
    toggleSideNav: (enable: boolean) => void;
    setNavItems: (navItems: NavItem[]) => void;
    getSideNavItems: (
        roles: Array<Roles>,
        features?: FeatureFlags | undefined,
        user?: string,
    ) => Omit<NavItem, 'href'>[];
    initializeNavigation: (
        navItems?: NavItem[],
        enable?: boolean,
        opened?: boolean,
    ) => void;
}

const NavContext = createContext<UseNav | undefined>(undefined);
const NavActionsContext = createContext<UseNavActions | undefined>(undefined);

NavContext.displayName = 'GrimsbyNavContext';

/**
 * SideNavProvider can be used to see if sideNav should be shown or not for a particular route location.
 * This was introduced to hide/show side nav based on route changes as sideNav is part of App.tsx and
 * it is not possible to listen to location changes there as history may not have been initialized
 * @param children
 * @constructor
 */

const GrimsbyNavProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const [sideNavEnabled] = useState(true);

    // Use centralized persisted state for navigation
    const [navState, setNavState] = usePersistedState<NavState>(
        { isNavOpened: true },
        {
            key: LOCAL_STORAGE_KEYS.NAV_STATE,
            defaultValue: { isNavOpened: true },
            validator: isNavState,
        },
    );

    const [navData, setNavData] = useState<NavData>({
        navItems: [],
        navItemsMap: new Map<string, NavItem>(),
    });
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbGroup.Item[]>([]);

    const navigationData = {
        navData,
        breadcrumbs,
        sideNavEnabled,
        isNavOpened: navState.isNavOpened,
    };

    const navigationActions = useMemo(() => {
        const toggleSideNav = (enable: boolean) => {
            setNavState((prevState) => ({ ...prevState, isNavOpened: enable }));
        };

        const setNavItems = (items: NavItem[]) => {
            setNavData({ navItems: items, navItemsMap: setRouteTree(items) });
        };

        const setRouteTree = (items: NavItem[]) => {
            const routingMap = new Map<string, NavItem>();
            items.forEach((item: NavItem) => {
                if (!routingMap.has(item.id)) {
                    routingMap.set(item.id, item);
                }
            });

            return routingMap;
        };

        const generateCrumbs = (
            id: string,
            params: { [key: string]: string },
        ): BreadcrumbGroup.Item[] => {
            if (!id) {
                return [];
            }

            const item = navData.navItemsMap.get(id);
            const paramsKeys = Array.from(Object.keys(params));

            if (item) {
                let crumbHref = item.href;

                if (paramsKeys.length) {
                    paramsKeys.forEach((param) => {
                        crumbHref = crumbHref.replace(
                            `:${param}`,
                            params[param],
                        );
                    });
                }

                const crumb = {
                    text: item.text,
                    href: crumbHref,
                } as BreadcrumbGroup.Item;

                if (item.parent) {
                    return [...generateCrumbs(item.parent, params), crumb];
                }

                return [crumb];
            }

            return [];
        };

        const generateBreadcrumbs = (
            path: string,
            params: { [key: string]: string },
        ) => {
            const item = navData.navItems.find((item) => item.href === path);

            if (item) {
                setBreadcrumbs(generateCrumbs(item.id, params));
            } else {
                setBreadcrumbs([]);
            }
        };

        const getSideNavItems = (
            roles: Array<Roles>,
            features?: FeatureFlags | undefined,
            user: string = '',
        ): Omit<NavItem, 'href'>[] => {
            const sideNavLinks = navData.navItems.filter((item) => {
                return (
                    check(roles, item.requiredPermissionAction) &&
                    checkFeature(user, item, features) &&
                    item.type === LinkTypes.Link &&
                    item.navType === NavType.SideNav
                );
            });
            const collapsibleItems = navData.navItems
                .filter((item) => {
                    return (
                        check(roles, item.requiredPermissionAction) &&
                        item.navType === NavType.SideNav &&
                        item.type !== LinkTypes.Link &&
                        checkFeature(user, item, features)
                    );
                })
                .sort((a, b) => {
                    if (
                        typeof a.ordinal !== 'undefined' &&
                        typeof b.ordinal !== 'undefined'
                    ) {
                        return a.ordinal - b.ordinal;
                    }

                    return -1;
                })
                .map((item) => {
                    const { href, ...navItem } = item;
                    return navItem;
                }) as Omit<NavItem, 'href'>[];
            if (collapsibleItems.length > 0) {
                collapsibleItems.forEach((item) => {
                    item.items = [] as NavItem[];

                    item.items = sideNavLinks.filter(
                        (child) => child.parent === item.id,
                    );

                    item.items.sort((a, b) => {
                        if (
                            item.useChildOrdinal &&
                            typeof a.ordinal !== 'undefined' &&
                            typeof b.ordinal !== 'undefined'
                        ) {
                            return a.ordinal - b.ordinal;
                        }
                        const aText = a.text.toLowerCase();
                        const bText = b.text.toLowerCase();

                        return aText < bText ? -1 : aText > bText ? 1 : 0;
                    });

                    item.redirectTo = item.items[0]?.href;
                });

                return collapsibleItems;
            }
            return sideNavLinks;
        };

        const initializeNavigation = (
            navItems: NavItem[] = [],
            enable: boolean = true,
            opened?: boolean,
        ) => {
            setNavItems(navItems);

            if (typeof opened === 'boolean') {
                setNavState((prevState) => ({
                    ...prevState,
                    isNavOpened: opened,
                }));
            }
        };

        return {
            toggleSideNav,
            setNavItems,
            generateBreadcrumbs,
            getSideNavItems,
            initializeNavigation,
        };
    }, [navData.navItems, navData.navItemsMap]);

    return (
        <NavContext.Provider value={navigationData}>
            <NavActionsContext.Provider value={navigationActions}>
                {children}
            </NavActionsContext.Provider>
        </NavContext.Provider>
    );
};

const useGrimsbyNav = (): UseNav => {
    const context = useContext(NavContext);

    if (typeof context === 'undefined') {
        throw new Error(
            'useGrimsbyNav should be used within a NavContext. Wrap component with SideNavProvider.',
        );
    }

    return context;
};

const useGrimsbyNavActions = (): UseNavActions => {
    const context = useContext(NavActionsContext);

    if (typeof context === 'undefined') {
        throw new Error(
            'useGrimsbyNavActions should be used within a NavContext. Wrap component with SideNavProvider.',
        );
    }

    return context;
};

export { GrimsbyNavProvider, useGrimsbyNav, useGrimsbyNavActions };
